/**
 * Toast notifications — global, lightweight, stacked above the tab bar.
 *
 * Use:
 *   import { toast } from '../components/toast.js';
 *   toast.success('Withdrawal sent');
 *   toast.error('Could not load balance', { sticky: true });
 *
 * Single host element is created lazily and lives outside the route-shell so
 * SPA navigation doesn't clear in-flight notifications.
 */

let host = null;

function ensureHost() {
  if (host) return host;
  host = document.createElement('div');
  host.className = 'toast-host';
  host.setAttribute('role', 'status');
  host.setAttribute('aria-live', 'polite');
  document.body.appendChild(host);
  return host;
}

function show(kind, message, { sticky = false, timeout = 3200 } = {}) {
  const h = ensureHost();
  const el = document.createElement('div');
  el.className = `toast ${kind}`;
  el.textContent = message;
  h.appendChild(el);
  if (!sticky) {
    setTimeout(() => {
      el.style.opacity = '0';
      el.style.transition = 'opacity 200ms';
      setTimeout(() => el.remove(), 240);
    }, timeout);
  }
  // Tap to dismiss.
  el.addEventListener('click', () => el.remove());
  return () => el.remove();
}

export const toast = {
  info: (msg, opts) => show('info', msg, opts),
  success: (msg, opts) => show('success', msg, opts),
  error: (msg, opts) => show('error', msg, opts),
  warn: (msg, opts) => show('warn', msg, opts),
  /** Pull a useful message out of an ApiError (or fall back to generic text). */
  fromError(err, fallback = 'Something went wrong.') {
    const msg = err && err.message ? err.message : fallback;
    return show('error', msg);
  },
};
